import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { X, ChevronRight, ChevronLeft, ArrowRight } from 'lucide-react';
import useScrollAnimation from '../hooks/useScrollAnimation';

// Scroll Animation Wrapper Component
const ScrollReveal = ({ children, delay = 0, className = '' }) => {
  const [ref, isVisible] = useScrollAnimation(0.1);
  return (
    <div 
      ref={ref} 
      className={`transition-all duration-1000 ${className} ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const services = [
  {
    id: 'bookkeeping',
    title: 'Book-keeping Services',
    tagline: 'Financial clarity that scales with your business',
    description: 'Comprehensive book-keeping solutions for US startups and SMBs with accurate financial records.',
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&h=800&fit=crop',
    details: {
      intro: 'We offer book-keeping services that scales with you. Our team ensures your financial records are accurate, compliant, and always up-to-date.',
      longDescription: 'In today\'s fast-paced business environment, maintaining accurate financial records is crucial for making informed decisions. Our book-keeping services provide you with real-time visibility into your financial health, allowing you to focus on what matters most—growing your business.',
      services: [
        'Accounts Payable & Receivable Management',
        'Bank & Credit Card Reconciliation',
        'General Ledger Maintenance',
        'Financial Statement Preparation',
        'Payroll Processing Support',
        'Month-End & Year-End Closing',
        'Cash Flow Management',
        'Budget vs Actual Analysis'
      ]
    }
  },
  {
    id: 'audit',
    title: 'Audit & Assurance Services',
    tagline: 'Integrity and professional standards you can trust',
    description: 'Our services can be benchmarked with the highest degree of Integrity and professional standards.',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=800&fit=crop',
    details: {
      intro: 'Comprehensive audit and assurance services that provide stakeholders with confidence in your financial reporting.',
      longDescription: 'Our audit and assurance practice is built on a foundation of integrity, objectivity, and professional skepticism. We work closely with management to understand your business, identify risks, and provide actionable insights that go beyond compliance.',
      services: [
        'Statutory Audits',
        'Internal Audits',
        'Tax Audits',
        'Due Diligence Reviews',
        'Compliance Audits',
        'Financial Statement Audits',
        'SOC 1 & SOC 2 Readiness',
        'Risk Assessment & Management'
      ]
    }
  },
  {
    id: 'india-entry',
    title: 'India Entry Strategy',
    tagline: 'Your launchpad for expanding into India',
    description: 'Structured launchpad for US companies expanding into India with complete regulatory support.',
    image: 'https://images.unsplash.com/photo-1524492412937-b28074a5d7da?w=1200&h=800&fit=crop',
    details: {
      intro: 'End-to-end support for your India expansion. We handle the complexities so you can focus on growth.',
      longDescription: 'Expanding into India presents immense opportunities but also unique challenges. Our India Entry Strategy service provides a structured approach to market entry, ensuring compliance with local regulations while optimizing your operational setup for success.',
      services: [
        'Entity Structure Advisory',
        'Company Registration & Incorporation',
        'Regulatory Compliance Setup',
        'Tax Registration (GST, PAN, TAN)',
        'Banking & Financial Setup',
        'HR & Payroll Infrastructure',
        'Legal Documentation',
        'Ongoing Compliance Management'
      ]
    }
  },
  {
    id: 'tax',
    title: 'Tax Preparation Services',
    tagline: 'Expert compliance with US tax regulations',
    description: 'Expert tax preparation and filing services ensuring compliance with all US tax regulations.',
    image: 'https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=1200&h=800&fit=crop',
    details: {
      intro: 'Complete tax preparation and planning services that maximize your returns while ensuring full compliance.',
      longDescription: 'Navigating the complexities of US tax law requires expertise and attention to detail. Our tax preparation services combine deep technical knowledge with a proactive approach to tax planning, helping you minimize liabilities and avoid costly penalties.',
      services: [
        'Federal & State Tax Preparation',
        'Corporate Tax Returns',
        'Individual Tax Returns',
        'FBAR Compliance',
        'Tax Planning & Strategy',
        'IRS Representation',
        'Multi-State Tax Filing',
        'International Tax Compliance'
      ]
    }
  }
];

const ServicesPage = () => {
  const location = useLocation();
  
  // Parse URL params immediately for initial state
  const getInitialState = () => {
    const params = new URLSearchParams(location.search);
    const serviceIndex = params.get('service');
    if (serviceIndex !== null) {
      const index = parseInt(serviceIndex, 10);
      if (index >= 0 && index < services.length) {
        return { index, openModal: true, service: services[index] };
      }
    }
    return { index: 0, openModal: false, service: null };
  };
  
  const initialState = getInitialState();
  
  const [currentIndex, setCurrentIndex] = useState(initialState.index);
  const [modalOpen, setModalOpen] = useState(initialState.openModal);
  const [selectedService, setSelectedService] = useState(initialState.service);
  const containerRef = useRef(null);
  const isScrolling = useRef(false);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Set body overflow on initial load if modal is open
  useEffect(() => {
    if (initialState.openModal) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Navigate to next/previous slide
  const goNext = useCallback(() => {
    if (currentIndex < services.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  }, [currentIndex]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  }, [currentIndex]);

  // Handle wheel scroll for desktop
  useEffect(() => {
    const handleWheel = (e) => {
      if (modalOpen) return;
      
      e.preventDefault();
      
      if (isScrolling.current) return;
      isScrolling.current = true;

      if (e.deltaY > 0) {
        goNext();
      } else if (e.deltaY < 0) {
        goPrev();
      }

      setTimeout(() => {
        isScrolling.current = false;
      }, 800);
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (container) {
        container.removeEventListener('wheel', handleWheel);
      }
    };
  }, [modalOpen, goNext, goPrev]);

  // Handle touch swipe for mobile
  useEffect(() => {
    const handleTouchStart = (e) => {
      if (modalOpen) return;
      touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchMove = (e) => {
      if (modalOpen) return;
      touchEndX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = () => {
      if (modalOpen) return;
      if (isScrolling.current) return;
      
      const swipeDistance = touchStartX.current - touchEndX.current;
      const minSwipeDistance = 50; // Minimum swipe distance in pixels
      
      if (Math.abs(swipeDistance) > minSwipeDistance) {
        isScrolling.current = true;
        
        if (swipeDistance > 0) {
          // Swiped left - go to next
          goNext();
        } else {
          // Swiped right - go to previous
          goPrev();
        }
        
        setTimeout(() => {
          isScrolling.current = false;
        }, 800);
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('touchstart', handleTouchStart, { passive: true });
      container.addEventListener('touchmove', handleTouchMove, { passive: true });
      container.addEventListener('touchend', handleTouchEnd, { passive: true });
    }

    return () => {
      if (container) {
        container.removeEventListener('touchstart', handleTouchStart);
        container.removeEventListener('touchmove', handleTouchMove);
        container.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [modalOpen, goNext, goPrev]);

  const handleKeyDown = useCallback((e) => {
    if (modalOpen) {
      if (e.key === 'Escape') closeModal();
      return;
    }
    
    if (e.key === 'ArrowRight') {
      goNext();
    } else if (e.key === 'ArrowLeft') {
      goPrev();
    }
  }, [modalOpen, goNext, goPrev]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const openModal = (service) => {
    setSelectedService(service);
    setModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedService(null);
    document.body.style.overflow = 'unset';
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      <Navigation />
      
      {/* Main Carousel Container */}
      <div 
        ref={containerRef}
        className="h-screen overflow-hidden"
        tabIndex={0}
      >
        {/* Slides Container */}
        <div 
          className="flex h-full transition-transform duration-700 ease-in-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {services.map((service, index) => (
            <div 
              key={service.id}
              className="min-w-full h-full flex-shrink-0 cursor-pointer group"
              onClick={() => openModal(service)}
            >
              {/* Full-screen gradient slide */}
              <div className="h-full relative overflow-hidden">
                {/* Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                  style={{ backgroundImage: `url(${service.image})` }}
                />
                
                {/* Gradient Overlay - Dark gradient from left */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a] via-[#1a1a1a]/80 to-transparent" />
                <div className="absolute inset-0 bg-[#3E2723]/30" />
                
                {/* Content */}
                <div className="relative h-full flex flex-col justify-center px-8 md:px-20 lg:px-32 pt-16">
                  <div className="max-w-2xl">
                    {/* Service Number */}
                    <div className="flex items-center space-x-4 mb-6 md:mb-8">
                      <div className="w-12 h-[1px] bg-white/50"></div>
                      <span className="text-white/70 text-sm tracking-[0.3em] uppercase font-light">
                        {String(index + 1).padStart(2, '0')} / {String(services.length).padStart(2, '0')}
                      </span>
                    </div>
                    
                    {/* Title */}
                    <h1 className="text-3xl md:text-5xl lg:text-7xl font-bold text-white mb-4 md:mb-6 leading-[1.1] tracking-tight">
                      {service.title}
                    </h1>
                    
                    {/* Tagline */}
                    <p className="text-lg md:text-2xl lg:text-3xl text-white/80 font-light mb-6 md:mb-10 leading-relaxed max-w-xl">
                      {service.tagline}
                    </p>
                    
                    {/* Click to explore hint */}
                    <div className="flex items-center space-x-3 text-white/60 group-hover:text-white transition-colors duration-300">
                      <span className="text-sm md:text-base tracking-wider uppercase">Click to explore</span>
                      <ChevronRight className="w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Arrows - Hidden on mobile, use swipe instead */}
        <button
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          disabled={currentIndex === 0}
          className={`hidden md:flex absolute left-8 top-1/2 -translate-y-1/2 z-20 w-14 h-14 rounded-full border border-white/30 items-center justify-center transition-all duration-300 ${
            currentIndex === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-white hover:text-[#1a1a1a] text-white'
          }`}
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        
        <button
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          disabled={currentIndex === services.length - 1}
          className={`hidden md:flex absolute right-8 top-1/2 -translate-y-1/2 z-20 w-14 h-14 rounded-full border border-white/30 items-center justify-center transition-all duration-300 ${
            currentIndex === services.length - 1 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-white hover:text-[#1a1a1a] text-white'
          }`}
        >
          <ChevronRight className="w-6 h-6" />
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 md:bottom-12 left-1/2 -translate-x-1/2 z-20 flex items-center space-x-3">
          {services.map((_, index) => (
            <button
              key={index}
              onClick={(e) => { e.stopPropagation(); goToSlide(index); }}
              className={`transition-all duration-300 ${
                currentIndex === index 
                  ? 'w-12 h-2 bg-white rounded-full' 
                  : 'w-2 h-2 bg-white/40 rounded-full hover:bg-white/60'
              }`}
            />
          ))}
        </div>

        {/* Scroll/Swipe Hint */}
        <div className="absolute bottom-8 md:bottom-12 right-6 md:right-12 z-20 text-xs md:text-sm text-white/50 flex items-center space-x-2">
          <span className="md:hidden">Swipe to navigate</span>
          <span className="hidden md:inline">Scroll or use arrows</span>
        </div>
      </div>

      {/* Full Screen Modal - Direct to Content */}
      {modalOpen && selectedService && (
        <div className="fixed inset-0 z-50 bg-white overflow-y-auto">
          {/* Floating Navigation - Elegant Glass Design */}
          <div className="fixed top-0 left-0 right-0 z-50">
            <div className="mx-4 md:mx-8 mt-4 md:mt-6">
              <div className="max-w-6xl mx-auto flex items-center justify-between">
                {/* Back Button - Pill Style */}
                <button
                  onClick={closeModal}
                  className="group flex items-center space-x-2 bg-white/90 backdrop-blur-md px-4 py-2.5 rounded-full shadow-lg border border-gray-100 hover:bg-[#3E2723] hover:text-white hover:border-[#3E2723] transition-all duration-300"
                >
                  <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                  <span className="text-sm font-medium">Back to Services</span>
                </button>

                {/* Close Button - Minimal Circle */}
                <button
                  onClick={closeModal}
                  className="w-11 h-11 rounded-full bg-white/90 backdrop-blur-md shadow-lg border border-gray-100 hover:bg-[#3E2723] hover:text-white hover:border-[#3E2723] flex items-center justify-center transition-all duration-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="bg-white pt-20 md:pt-24">
            {/* Service Title Header */}
            <ScrollReveal className="max-w-6xl mx-auto px-6 md:px-16 pb-8 md:pb-12">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-[2px] bg-[#3E2723]"></div>
                <span className="text-xs font-medium tracking-[0.2em] text-[#3E2723]/60 uppercase">Service Details</span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                {selectedService.title}
              </h1>
              <p className="mt-4 text-lg md:text-xl text-[#3E2723]/80 font-light max-w-2xl">
                {selectedService.tagline}
              </p>
            </ScrollReveal>

            {/* Divider */}
            <div className="max-w-6xl mx-auto px-6 md:px-16">
              <div className="h-[1px] bg-gradient-to-r from-[#3E2723]/20 via-[#3E2723]/10 to-transparent"></div>
            </div>

            {/* Intro Section */}
            <ScrollReveal delay={100} className="max-w-6xl mx-auto px-6 md:px-16 py-12 md:py-16">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
                <div className="lg:col-span-4">
                  <h2 className="text-sm font-medium tracking-[0.2em] text-[#3E2723] uppercase mb-4">
                    Overview
                  </h2>
                  <div className="w-16 h-[2px] bg-[#3E2723]"></div>
                </div>
                <div className="lg:col-span-8">
                  <p className="text-2xl md:text-3xl lg:text-4xl text-gray-900 leading-snug font-light">
                    {selectedService.details.intro}
                  </p>
                </div>
              </div>
            </ScrollReveal>

            {/* Long Description */}
            <div className="bg-stone-50">
              <ScrollReveal delay={200} className="max-w-6xl mx-auto px-6 md:px-16 py-12 md:py-16">
                <p className="text-lg md:text-xl lg:text-2xl text-gray-600 leading-relaxed max-w-4xl">
                  {selectedService.details.longDescription}
                </p>
              </ScrollReveal>
            </div>

            {/* Key Services Section */}
            <ScrollReveal delay={100} className="max-w-6xl mx-auto px-6 md:px-16 py-12 md:py-20">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 mb-12">
                <div className="lg:col-span-4">
                  <h2 className="text-sm font-medium tracking-[0.2em] text-[#3E2723] uppercase mb-4">
                    Key Services
                  </h2>
                  <div className="w-16 h-[2px] bg-[#3E2723]"></div>
                </div>
                <div className="lg:col-span-8">
                  <p className="text-lg md:text-xl text-gray-600">
                    What we deliver for you
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 lg:gap-x-20 gap-y-0">
                {selectedService.details.services.map((service, index) => (
                  <div 
                    key={index}
                    className="flex items-center space-x-6 py-5 md:py-6 border-b border-gray-200 group cursor-pointer hover:border-[#3E2723] transition-colors"
                  >
                    <span className="text-[#3E2723]/40 group-hover:text-[#3E2723] font-light text-sm transition-colors">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <span className="text-lg md:text-xl text-gray-900 group-hover:text-[#3E2723] transition-colors font-light">
                      {service}
                    </span>
                  </div>
                ))}
              </div>
            </ScrollReveal>

            {/* CTA Section - Simple */}
            <div className="relative py-12 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-[#2d2420] via-[#3E2723] to-[#2d2420]"></div>
              
              <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-16 flex flex-col md:flex-row items-center justify-between">
                <div className="text-center md:text-left mb-6 md:mb-0">
                  <h2 className="text-2xl md:text-3xl font-bold text-white">
                    Let's talk.
                  </h2>
                  <p className="text-white/60 mt-1">Ready to get started with {selectedService.title.toLowerCase()}?</p>
                </div>
                <a href={`mailto:hello@trueledger.com?subject=Inquiry%20about%20${encodeURIComponent(selectedService.title)}&body=Hi%20Trueledger%20Team,%0A%0AI%20am%20interested%20in%20learning%20more%20about%20your%20${encodeURIComponent(selectedService.title)}%20services.%0A%0AThank%20you.`}>
                  <button className="group inline-flex items-center justify-center space-x-2 bg-white text-[#3E2723] px-8 py-4 hover:bg-[#8D6E63] hover:text-white transition-all duration-300">
                    <span className="font-medium">Contact Us</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesPage;
